import { useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { CalendarPlus, Receipt, Timer, BellRinging } from "@phosphor-icons/react";

const fmt = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const DAY = 86400000;

const hasCard = (t) => !!t.stripe_subscription_id || t.subscription?.gateway === "revolut";
export const isConsoleTrial = (t) => !!t && !t.is_master && !!t.trial_enforced && t.subscription_status === "trialing" && !hasCard(t);
const canInvoice = (t) => !!t && !t.is_master && !hasCard(t) && t.billing_mode !== "invoiced" && (isConsoleTrial(t) || t.billing_mode === "complimentary");

// Follow-up dialog: extend a console trial (7 / 14 days) or mark the workspace as subscribed by invoice
function FollowUpDialog({ tenant: t, mode, onClose, onDone }) {
  const [days, setDays] = useState(7);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const expired = t.trial_ends_at && new Date(t.trial_ends_at) <= Date.now();
  const newEnd = new Date((expired ? Date.now() : new Date(t.trial_ends_at).getTime()) + days * DAY);
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "extend") {
        const { data } = await API.post(`/tenants/${t.id}/trial/extend`, { days });
        toast.success(`${t.name}: trial ${data.unlocked ? "restarted" : "extended"} by ${days} days — now ends ${fmt(data.trial_ends_at)}`);
      } else {
        await API.post(`/tenants/${t.id}/convert-to-paid`, { reference, note });
        toast.success(`${t.name} marked as subscribed (invoiced) — full access confirmed`);
      }
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't update the trial"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="trial-followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{mode === "extend" ? <><CalendarPlus size={16} /> Extend free trial</> : <><Receipt size={16} /> Mark as subscribed (invoiced)</>}</DialogTitle>
          <DialogDescription>
            {mode === "extend"
              ? (expired ? `${t.name}'s trial lapsed on ${fmt(t.trial_ends_at)}. Extending restarts it from today and lifts the read-only lock straight away.`
                : `${t.name}'s trial currently ends ${fmt(t.trial_ends_at)}. The extension is added on to that date.`)
              : `${t.name} will show as an active subscription paid by invoice — no card checkout, the read-only lock lifts and no trial reminders are sent.`}
          </DialogDescription>
        </DialogHeader>
        {mode === "extend" ? (
          <div className="space-y-2">
            <Label className="text-xs">Extend by</Label>
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {[7, 14].map((d) => (
                <button key={d} type="button" role="radio" aria-checked={days === d} onClick={() => setDays(d)}
                  className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${days === d ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted/60"}`}
                  data-testid={`trial-extend-${d}`}>+{d} days</button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="trial-extend-preview">New end date: <strong>{fmt(newEnd)}</strong>. The workspace admins are e-mailed about the extension.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Invoice / PO reference <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. INV-2041" className="mt-1" maxLength={80} data-testid="trial-invoice-reference" />
            </div>
            <div>
              <Label className="text-xs">Internal note <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. annual, 10 seats, paid by BACS" className="mt-1" maxLength={300} data-testid="trial-invoice-note" />
            </div>
            <p className="text-xs text-muted-foreground">The workspace admins get a short "subscription active" e-mail. Their Billing page shows "Invoiced" instead of a checkout.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="trial-followup-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="trial-followup-confirm">{busy ? "Saving…" : mode === "extend" ? `Extend by ${days} days` : "Mark as subscribed"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Tenant card strip: console-trial status + sales follow-up actions (extend / mark as invoiced)
export function TenantTrialActions({ tenant: t, onChanged }) {
  const [mode, setMode] = useState(null);
  if (!t || t.is_master) return null;
  const trial = isConsoleTrial(t);
  const invoiced = t.billing_mode === "invoiced";
  if (!trial && !invoiced && !canInvoice(t)) return null;
  const ext = (t.trial_extensions || []).length;
  const left = t.trial_ends_at ? Math.ceil((new Date(t.trial_ends_at) - Date.now()) / DAY) : null;
  let status = null;
  if (invoiced) status = <>Subscribed · invoiced{t.invoiced?.reference ? ` · ref ${t.invoiced.reference}` : ""}{t.invoiced?.at ? ` · ${fmt(t.invoiced.at)}` : ""}{t.invoiced?.by_name ? ` by ${t.invoiced.by_name}` : ""}</>;
  else if (trial && left > 0) status = <>{t.trial_days}-day trial{ext ? ` (extended ×${ext})` : ""} · ends {fmt(t.trial_ends_at)}</>;
  else if (trial) status = <>Trial lapsed {fmt(t.trial_ends_at)} · read-only{t.trial_lapsed_alert_sent_at ? <span className="inline-flex items-center gap-0.5 ml-1 text-amber-700 dark:text-amber-400"><BellRinging size={9} weight="bold" /> sales alerted {fmt(t.trial_lapsed_alert_sent_at)}</span> : ""}</>;
  else status = <>Full access · no trial</>;
  return (
    <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground mb-2" data-testid={`trial-actions-${t.slug}`}>
      <span className="inline-flex items-center gap-1" data-testid={`trial-status-${t.slug}`}><Timer size={10} /> {status}</span>
      {trial && (
        <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] gap-1" onClick={() => setMode("extend")} data-testid={`trial-extend-btn-${t.slug}`}>
          <CalendarPlus size={11} /> Extend trial
        </Button>
      )}
      {canInvoice(t) && (
        <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] gap-1" onClick={() => setMode("invoice")} data-testid={`trial-invoice-btn-${t.slug}`}>
          <Receipt size={11} /> Mark as subscribed (invoiced)
        </Button>
      )}
      {mode && <FollowUpDialog tenant={t} mode={mode} onClose={() => setMode(null)} onDone={() => { setMode(null); onChanged?.(); }} />}
    </div>
  );
}
